// Main Print Dialog Component
// Sprint 2: PrintDialog with Tab-based UI

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/app/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { Button } from '@/app/components/ui/button';
import { Separator } from '@/app/components/ui/separator';
import { FileText, Settings, Info, Eye, Download, PanelLeft } from 'lucide-react';
import { PrintAreaTab } from './PrintAreaTab';
import { PrintFormatTab } from './PrintFormatTab';
import { PrintMetadataTab } from './PrintMetadataTab';
import { PrintPreviewTab } from './PrintPreviewTab';
import { PrintPresetsPanel } from './PrintPresetsPanel';
import type { UsePrintDialogOptions } from '@/app/hooks/usePrintDialog';
import { usePrintDialog } from '@/app/hooks/usePrintDialog';
import type { TableConfig } from '@/app/types/table';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface PrintDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tableConfig: TableConfig;
  projectName: string;
  projectData?: UsePrintDialogOptions['projectData'];
  author?: string;
}

export function PrintDialog({
  isOpen,
  onClose,
  tableConfig,
  projectName,
  projectData,
  author,
}: PrintDialogProps) {
  const { t } = useLanguage();
  const [showPresets, setShowPresets] = useState(true);
  
  const printDialog = usePrintDialog({
    tableConfig,
    projectName,
    projectData,
    author,
  });
  
  const {
    activeTab,
    setActiveTab,
    config,
    isExporting,
    presets,
    selectedPresetId,
    updateArea,
    updateFormat,
    updatePDFOptions,
    updateExcelOptions,
    updateMetadata,
    updatePreviewZoom,
    updatePageSize,
    loadPreset,
    saveAsPreset,
    deletePreset,
    handleExport,
    resetToDefaults,
    goToNextTab,
    goToPreviousTab,
  } = printDialog;
  
  const handleClose = () => {
    printDialog.closeDialog();
    onClose();
  };
  
  const isPreviewTab = activeTab === 'preview';
  const canGoNext = activeTab !== 'preview';
  const canGoPrevious = activeTab !== 'area';
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[1600px] h-[85vh] p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden mr-2 -ml-2"
              onClick={() => setShowPresets(!showPresets)}
            >
              <PanelLeft className="w-5 h-5" />
            </Button>
            <FileText className="w-5 h-5" />
            {t.table.printDialog.title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t.table.printDialog.title}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-1 overflow-hidden relative">
          {/* Left: Presets Panel - Responsive */}
          <div 
            className={`
              absolute lg:static top-0 left-0 bottom-0 z-20 
              w-72 border-r bg-muted/30 overflow-y-auto flex-shrink-0
              transition-transform duration-200 ease-in-out bg-white dark:bg-zinc-900
              ${showPresets ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
              lg:block shadow-xl lg:shadow-none
            `}
          >
            <PrintPresetsPanel
              presets={presets}
              selectedPresetId={selectedPresetId}
              currentConfig={config}
              onLoadPreset={loadPreset}
              onSavePreset={saveAsPreset}
              onDeletePreset={deletePreset}
              onResetDefaults={resetToDefaults}
            />
          </div>
          
          <div className="flex-1 flex flex-col overflow-hidden w-full">
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as typeof activeTab)}
              className="flex-1 flex flex-col overflow-hidden"
            >
              <div className="px-6 pt-5 pb-3 border-b bg-background overflow-x-auto no-scrollbar">
                <TabsList className="flex flex-row w-full lg:w-auto gap-2.5 h-auto justify-start p-1.5 rounded-lg min-w-max">
                  <TabsTrigger value="area" className="flex items-center gap-2.5 flex-1 px-5 py-3 min-h-[48px] rounded-lg">
                    <FileText className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium">{t.table.printDialog.tabs.area}</span>
                  </TabsTrigger>
                  <TabsTrigger value="format" className="flex items-center gap-2.5 flex-1 px-5 py-3 min-h-[48px] rounded-lg">
                    <Settings className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium">{t.table.printDialog.tabs.format}</span>
                  </TabsTrigger>
                  <TabsTrigger value="metadata" className="flex items-center gap-2.5 flex-1 px-5 py-3 min-h-[48px] rounded-lg">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium">{t.table.printDialog.tabs.metadata}</span>
                  </TabsTrigger>
                  <TabsTrigger value="preview" className="flex items-center gap-2.5 flex-1 px-5 py-3 min-h-[48px] rounded-lg">
                    <Eye className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium">{t.table.printDialog.tabs.preview}</span>
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                <TabsContent value="area" className="mt-0 p-10">
                  <PrintAreaTab
                    area={config.area}
                    tableConfig={tableConfig}
                    onUpdateArea={updateArea}
                  />
                </TabsContent>
                
                <TabsContent value="format" className="mt-0 p-10">
                  <PrintFormatTab
                    format={config.format}
                    onUpdateFormat={updateFormat}
                    onUpdatePDFOptions={updatePDFOptions}
                    onUpdateExcelOptions={updateExcelOptions}
                    onUpdatePageSize={updatePageSize}
                  />
                </TabsContent>
                
                <TabsContent value="metadata" className="mt-0 p-10">
                  <PrintMetadataTab
                    metadata={config.metadata}
                    onUpdateMetadata={updateMetadata}
                  />
                </TabsContent>
                
                <TabsContent value="preview" className="mt-0 p-10">
                  <PrintPreviewTab
                    config={config}
                    tableConfig={tableConfig}
                    zoom={config.preview.zoom}
                    onUpdateZoom={updatePreviewZoom}
                  />
                </TabsContent>
              </div>
            </Tabs>
            
            {/* Footer: Navigation & Export */}
            <Separator />
            <div className="px-6 py-5 bg-background flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={goToPreviousTab}
                  disabled={!canGoPrevious}
                  className="min-w-[100px]"
                >
                  {t.table.printDialog.actions.back}
                </Button>
                <Button
                  variant="outline"
                  onClick={goToNextTab}
                  disabled={!canGoNext}
                  className="min-w-[100px]"
                >
                  {t.table.printDialog.actions.next}
                </Button>
              </div>
              
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={handleClose} className="min-w-[100px]">
                  {t.table.printDialog.actions.cancel}
                </Button>
                <Button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="min-w-[140px]"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isExporting
                    ? t.table.printDialog.actions.exporting
                    : isPreviewTab
                    ? t.table.printDialog.actions.exportPreview
                    : t.table.printDialog.actions.export}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}